import React, { useState } from 'react';
import { Importer, ImporterField } from 'react-csv-importer';
import Modal from 'components/elements/Modal';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useSaveCSV from './crud/useSaveCSV';
import DownloadsIcon from 'assets/Icons/download/DownloadsIcon';

// theme CSS for React CSV Importer
import 'react-csv-importer/dist/index.css';
import CSVRoleSelectionForm from './csvRoleSelectionForm';

const ImportCSVSchema = yup.object().shape({
  buildingUuid: yup.string().required('Building is required'),
  accountUuid: yup.string().required('Account is required'),
  integrationUuid: yup.string().required('Integration is required')
});

export default function ImportCSV({
  showModal,
  setShowModal,
  entityName,
  importQuery,
  queryName
}: any) {
  const [resultCSV, setResultCSV] = useState<any>();

  const methods = useForm({
    defaultValues: {
      buildingUuid: '',
      accountUuid: '',
      integrationUuid: ''
    },
    resolver: yupResolver(ImportCSVSchema)
  });

  const saveCSVMutation = useSaveCSV({
    importQuery,
    queryName,
    onSuccess: (result: any) => {
      setResultCSV(result);
    }
  });

  /**
   * This function creates a downloadable link for a CSV file and triggers a download when clicked.
   */
  const downloadFile = () => {
    const url = window.URL.createObjectURL(new Blob([resultCSV]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'yourfilename.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Modal
      isOpen={showModal}
      onRequestClose={() => {
        setShowModal(false);
        // if (setEditUUID) setEditUUID('');
      }}
      title={entityName}
    >
      <CSVRoleSelectionForm Controller={Controller} methods={methods} />
      <br />
      <div className="w-full">
        <Importer
          dataHandler={async (rows) => {
            // required, receives a list of parsed objects based on defined fields and user column mapping;
            // may be called several times if file is large
            // (if this callback returns a promise, the widget will wait for it before parsing more data)
            console.log('received batch of rows', rows);

            // mock timeout to simulate processing
            await new Promise((resolve) => setTimeout(resolve, 500));
          }}
          chunkSize={10000} // optional, internal parsing chunk size in bytes
          defaultNoHeader={false} // optional, keeps "data has headers" checkbox off by default
          restartable={false} // optional, lets user choose to upload another file when import is complete
          onStart={({ file, fields }) => {
            // optional, invoked when user has mapped columns and started import
            console.log('starting import of file', file, 'with fields', fields);
          }}
          onComplete={({ file, fields }) => {
            // optional, invoked right after import is done (but user did not dismiss/reset the widget yet)
            console.log('finished import of file', file, 'with fields', fields);

            saveCSVMutation.mutate({
              accountUuid: methods.watch('accountUuid'),
              buildingUuid: methods.watch('buildingUuid'),
              integrationUuid: methods.watch('integrationUuid'),
              row: file
            });
          }}
          onClose={() => {
            // optional, invoked when import is done and user clicked "Finish"
            // (if this is not specified, the widget lets the user upload another file)
            console.log('importer dismissed');
            setShowModal(false);
          }}
        >
          <div className="w-[55rem]">
            <ImporterField name="organization" label="Organization" />
            <ImporterField name="firstName" label="First Name" />
            <ImporterField name="lastName" label="Last Name" />
            <ImporterField name="email" label="Email" />
            <ImporterField name="phoneNumber" label="Phone Number" />
            <ImporterField name="links" label="Links" optional />
            <ImporterField name="facilityCode" label="Facility Code" optional />
            <ImporterField name="badgeId" label="Badge Id" optional />
          </div>
        </Importer>
        {resultCSV && (
          <div className="flex items-center grid justify-items-end">
            <button
              className="mt-3 bg-neutral2 font-bold py-2 px-4 rounded inline-flex items-center"
              onClick={downloadFile}
            >
              <DownloadsIcon />
              <span>Download</span>
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
